import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || `\${API_BASE_URL}`;

interface Creator {
  id: string;
  displayName: string;
  username: string;
  avatarUrl?: string;
}

const CommissionRequestPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const creatorIdParam = searchParams.get('creatorId');

  const [creatorId, setCreatorId] = useState(creatorIdParam || '');
  const [creatorSearch, setCreatorSearch] = useState('');
  const [creators, setCreators] = useState<Creator[]>([]);
  const [selectedCreator, setSelectedCreator] = useState<Creator | null>(null);
  const [searchLoading, setSearchLoading] = useState(false);

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [amount, setAmount] = useState('');
  const [deadline, setDeadline] = useState('');
  const [referenceFiles, setReferenceFiles] = useState<FileList | null>(null);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (creatorIdParam) {
      fetchCreatorDetails(creatorIdParam);
    }
  }, [creatorIdParam]);

  const fetchCreatorDetails = async (id: string) => {
    try {
      const token = localStorage.getItem('accessToken');
      const res = await axios.get(`${API_BASE_URL}/api/creators/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSelectedCreator(res.data.creator);
    } catch (err) {
      console.error('Failed to fetch creator:', err);
    }
  };

  const searchCreators = async () => {
    if (!creatorSearch.trim()) return;
    try {
      setSearchLoading(true);
      const token = localStorage.getItem('accessToken');
      const res = await axios.get(`${API_BASE_URL}/api/creators`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { search: creatorSearch },
      });
      setCreators(res.data.creators || []);
    } catch (err) {
      console.error('Failed to search creators:', err);
    } finally {
      setSearchLoading(false);
    }
  };

  const handleSelectCreator = (creator: Creator) => {
    setSelectedCreator(creator);
    setCreatorId(creator.id);
    setCreators([]);
    setCreatorSearch('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!creatorId || !title || !description || !amount) {
      setError('Please fill in all required fields');
      return;
    }

    try {
      setLoading(true);
      setError('');

      const formData = new FormData();
      formData.append('creatorId', creatorId);
      formData.append('title', title);
      formData.append('description', description);
      formData.append('amount', (parseFloat(amount) * 100).toString());
      if (deadline) {
        formData.append('deadline', new Date(deadline).toISOString());
      }
      if (referenceFiles) {
        Array.from(referenceFiles).forEach((file) => {
          formData.append('referenceFiles', file);
        });
      }

      const token = localStorage.getItem('accessToken');
      await axios.post(`${API_BASE_URL}/api/commissions`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data',
        },
      });

      navigate('/dashboard/commissions/requests');
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to submit commission request');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-6">
        <button
          onClick={() => navigate(-1)}
          className="text-blue-600 hover:text-blue-800 mb-4 inline-flex items-center"
        >
          ← Back
        </button>
        <h1 className="text-3xl font-bold text-gray-900">Request a Commission</h1>
        <p className="text-gray-600 mt-2">
          Submit a custom commission request to your favorite creator
        </p>
      </div>

      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">{error}</p>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Creator Selection */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Select Creator</h2>
          
          {selectedCreator ? (
            <div className="flex items-center justify-between p-4 bg-blue-50 rounded-lg">
              <div className="flex items-center gap-3">
                {selectedCreator.avatarUrl && (
                  <img
                    src={selectedCreator.avatarUrl}
                    alt={selectedCreator.displayName}
                    className="w-12 h-12 rounded-full object-cover"
                  />
                )}
                <div>
                  <p className="font-medium text-gray-900">{selectedCreator.displayName}</p>
                  <p className="text-sm text-gray-600">@{selectedCreator.username}</p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => {
                  setSelectedCreator(null);
                  setCreatorId('');
                }}
                className="text-sm text-red-600 hover:text-red-800"
              >
                Change
              </button>
            </div>
          ) : (
            <div>
              <div className="flex gap-2 mb-4">
                <input
                  type="text"
                  value={creatorSearch}
                  onChange={(e) => setCreatorSearch(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), searchCreators())}
                  placeholder="Search for creators..."
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                />
                <button
                  type="button"
                  onClick={searchCreators}
                  disabled={searchLoading}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
                >
                  Search
                </button>
              </div>
              
              {creators.length > 0 && (
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {creators.map((creator) => (
                    <button
                      key={creator.id}
                      type="button"
                      onClick={() => handleSelectCreator(creator)}
                      className="w-full flex items-center gap-3 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 text-left"
                    >
                      {creator.avatarUrl && (
                        <img
                          src={creator.avatarUrl}
                          alt={creator.displayName}
                          className="w-10 h-10 rounded-full object-cover"
                        />
                      )}
                      <div>
                        <p className="font-medium text-gray-900">{creator.displayName}</p>
                        <p className="text-sm text-gray-600">@{creator.username}</p>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Commission Details */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Commission Details</h2>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Title <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                required
                placeholder="e.g., Custom character artwork"
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Description <span className="text-red-500">*</span>
              </label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                required
                rows={6}
                placeholder="Describe what you want the creator to make for you..."
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
              />
              <p className="text-xs text-gray-500 mt-1">
                Be as detailed as possible to help the creator understand your vision
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Budget (USD) <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  required
                  min="1"
                  step="0.01"
                  placeholder="50.00"
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Funds will be held in escrow until completion
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Deadline (Optional)
                </label>
                <input
                  type="date"
                  value={deadline}
                  onChange={(e) => setDeadline(e.target.value)}
                  min={new Date().toISOString().split('T')[0]}
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Reference Files (Optional)
              </label>
              <input
                type="file"
                multiple
                onChange={(e) => setReferenceFiles(e.target.files)}
                accept="image/*,.pdf"
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
              />
              <p className="text-xs text-gray-500 mt-1">
                Upload images or PDFs to help explain your vision
              </p>
            </div>
          </div>
        </div>

        {/* Important Note */}
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <h3 className="font-medium text-yellow-900 mb-2">📌 Important Notes</h3>
          <ul className="text-sm text-yellow-800 space-y-1 list-disc list-inside">
            <li>Your payment will be held in escrow until the creator completes the work</li>
            <li>The creator can accept, negotiate, or decline your request</li>
            <li>You'll be notified when the creator responds</li>
            <li>You'll need to authorize payment once the creator accepts</li>
            <li>Funds are only released after you approve the final deliverables</li>
          </ul>
        </div>

        {/* Submit */}
        <div className="flex gap-3">
          <button
            type="submit"
            disabled={loading || !creatorId}
            className="flex-1 px-6 py-3 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 font-medium"
          >
            {loading ? 'Submitting...' : 'Submit Commission Request'}
          </button>
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="px-6 py-3 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400"
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
};

export default CommissionRequestPage;
